import {
  DOCUMENT_FIELDS_PATH,
  type EntityFieldItemsUpdater,
  type EntityOperation,
  isEntityOperation,
} from '@github-ui/optimistic-connection'
import {ENTITY_FIELDS_OWNER} from '@github-ui/optimistic-connection/pendingOperations'
import {notifySettlement} from '@github-ui/optimistic-connection/settlementRegistry'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import {buildSubIssueOptimisticNode} from '@github-ui/sub-issues/sub-issue-optimistic-node'
import {useCallback, useRef} from 'react'

export type ParentSubIssueUpdateVariables = {
  issueId: string
  issueTitle: string
  issueNumber: number
  issueDatabaseId: number | null
  issueState: string
  nameWithOwner: string
  currentParentId?: string
}

export type ParentSubIssueUpdates = {
  confirm: () => Promise<void>
  rollback: (error: Error) => Promise<void>
}

type Deferred<T> = {
  promise: Promise<T>
  resolve: (value: T | PromiseLike<T>) => void
  reject: (reason?: unknown) => void
}

let parentOperationSequence = 0

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value)
}

export const updateParentSubIssues: EntityFieldItemsUpdater = (entity, operation) => {
  if (operation.field !== 'subIssues' || !isRecord(entity.subIssues) || !Array.isArray(entity.subIssues.nodes)) {
    return false
  }

  const nodes = entity.subIssues.nodes as Array<Record<string, unknown>>
  const removedKeys = new Set(operation.removeKeys)
  const removed = nodes.filter(node => typeof node.id === 'string' && removedKeys.has(node.id))
  const next = nodes.filter(node => typeof node.id !== 'string' || !removedKeys.has(node.id))
  let addedCompleted = 0

  for (const {key, item} of operation.upserts) {
    const existingIndex = next.findIndex(node => node.id === key)
    if (existingIndex !== -1) next.splice(existingIndex, 1)
    else if (item.closed === true) addedCompleted += 1
    next.push(item)
  }

  const countDelta = next.length - nodes.length
  const completedDelta = addedCompleted - removed.filter(node => node.closed === true).length
  entity.subIssues.nodes = next

  if (isRecord(entity.subIssuesConnection) && typeof entity.subIssuesConnection.totalCount === 'number') {
    entity.subIssuesConnection.totalCount = Math.max(0, entity.subIssuesConnection.totalCount + countDelta)
  }
  if (
    isRecord(entity.subIssuesSummary) &&
    typeof entity.subIssuesSummary.total === 'number' &&
    typeof entity.subIssuesSummary.completed === 'number'
  ) {
    const total = Math.max(0, entity.subIssuesSummary.total + countDelta)
    entity.subIssuesSummary.total = total
    entity.subIssuesSummary.completed = Math.min(total, Math.max(0, entity.subIssuesSummary.completed + completedDelta))
  }

  return countDelta !== 0 || completedDelta !== 0
}

async function ignoreCompanionFailure(mutation: Promise<unknown>): Promise<void> {
  try {
    await mutation
  } catch {
    // The primary set-parent mutation reports the same failure to the caller.
  }
}

export function useParentSubIssueUpdates({
  issueId,
  issueTitle,
  issueNumber,
  issueDatabaseId,
  issueState,
  nameWithOwner,
  currentParentId,
}: ParentSubIssueUpdateVariables) {
  const knownParentIdsRef = useRef(new Set(currentParentId ? [currentParentId] : []))
  const deferredsRef = useRef(new WeakMap<EntityOperation, Deferred<EntityOperation>>())
  const {mutateAsync} = useConnectionMutation<EntityOperation>({
    ownerId: ENTITY_FIELDS_OWNER,
    connectionPath: DOCUMENT_FIELDS_PATH,
    scope: {id: `set-parent-companion:${issueId}`},
    mutationFn: operation => {
      if (!isEntityOperation(operation)) throw new Error('Parent update must target entity fields')
      const deferred = deferredsRef.current.get(operation)
      if (!deferred) throw new Error('Parent update was not registered')
      return deferred.promise
    },
    onSuccess: (operation, variables) =>
      notifySettlement(operation, ENTITY_FIELDS_OWNER, DOCUMENT_FIELDS_PATH, variables.operationId, {
        forceRevalidate: true,
      }),
  })

  return useCallback(
    (nextParentId: string): ParentSubIssueUpdates => {
      if (currentParentId) knownParentIdsRef.current.add(currentParentId)
      const child = buildSubIssueOptimisticNode({
        id: issueId,
        title: issueTitle,
        nameWithOwner,
        number: issueNumber,
        state: issueState,
        databaseId: issueDatabaseId,
      })
      const operations: EntityOperation[] = [
        ...[...knownParentIdsRef.current]
          .filter(parentId => parentId !== nextParentId)
          .map<EntityOperation>(parentId => ({
            type: 'updateEntityFieldItems',
            id: parentId,
            field: 'subIssues',
            upserts: [],
            removeKeys: [issueId],
            countDelta: -1,
          })),
        {
          type: 'updateEntityFieldItems',
          id: nextParentId,
          field: 'subIssues',
          upserts: [{key: issueId, item: child}],
          removeKeys: [],
          countDelta: 1,
        },
      ]
      knownParentIdsRef.current.add(nextParentId)

      const updates = operations.map(operation => {
        const deferred = Promise.withResolvers<EntityOperation>()
        deferredsRef.current.set(operation, deferred)
        const mutation = mutateAsync({
          operation,
          operationId: `set-parent-${parentOperationSequence++}`,
        })
        void ignoreCompanionFailure(mutation)
        return {deferred, mutation, operation}
      })

      return {
        confirm: async () => {
          for (const {deferred, operation} of updates) deferred.resolve(operation)
          await Promise.all(updates.map(update => update.mutation))
        },
        rollback: async error => {
          for (const {deferred} of updates) deferred.reject(error)
          await Promise.allSettled(updates.map(update => update.mutation))
        },
      }
    },
    [currentParentId, issueDatabaseId, issueId, issueNumber, issueState, issueTitle, mutateAsync, nameWithOwner],
  )
}
